import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import TpRectangleCard from "./TpRectangleCard";
import TpSuqareCard from "./TpSuqareCard";
import { PiFireLight } from "react-icons/pi";
import { PiNewspaperClipping } from "react-icons/pi";
import { PiAtomDuotone } from "react-icons/pi";
import useStore from "../../../store/useStore";
import { useNavigate } from "react-router-dom";
import {
  likeContent,
  getContentCounts,
  getLikeBookmarkedContent,
  bookmarkContent,
  getGroupedContentByFormat,
} from "../service";
import { ContentItem } from "../../../utils/contentMappers";
import debounce from "lodash/debounce";
import EmptyState from "../../../assets/images/EmptyState.svg";
import { getDecodedToken, getLocalStorage } from "../../../utils/localStorage";
import { AUTH_TOKEN } from "../../../utils/constants";
import LoadingSkeleton from "./states/LoadingSkeleton";

interface NewContentTypeSectionProps {
  contentType?: string;
  contentTypeId?: number | number[];
}

// Update the interface for content counts
interface ContentCounts {
  [key: string]: {
    isExternalSharable: number;
    like_count: number;
    uploadThumbnailUrl: string;
    previewUrl: string;
    readTime: string;
    duration: string;
  };
}

// Function to transform grouped content by format response to desired format
const transformGroupedContentByFormatResponse = (response: any) => {
  if (!response?.grouped?.contentFormatId?.groups) {
    return [];
  }

  const transformedData = response.grouped.contentFormatId.groups.flatMap((group: any) => {
    // Return all documents from each group, not just the first one
    return group.doclist.docs.map((doc: any) => ({
      id: doc.id,
      docId: doc.docId,
      contentTitle: doc.contentTitle,
      contentType: Array.isArray(doc.contentFormat) ? doc.contentFormat[0] || "documents" : doc.contentFormat || "documents", // Use contentFormat instead of contentType
      contentTypeId: doc.contentTypeId,
      contentDescription: doc.contentDescription,
      uploadThumbnailUrl: doc.uploadThumbnailUrl,
      coverPhotoUrl: "",
      duration: doc.duration,
      isLike: false,
      isBookmarked: false,
      viewCount: doc.view_count || 0,
      like_count: doc.like_count || 0,
      shareCount: doc.share_count || 0,
      contentGenreId: doc.contentGenreId || [],
      genres: [],
      publishedDate: doc.publishedDate,
      deepLinkCode: doc.deepLinkCode,
      consolidatedModule: doc.consolidatedModule || "",
      contentFormat: doc.contentFormat || "",
    }));
  });

  return transformedData;
};

// Function to get display name from code name
const getDisplayName = (codeName: string) => {
  const newContentTypes = [
    { id: 6003, code: "Clinical Advances & Conference Updates", displayName: "Clinical Updates", pmEnabled: false },
    { id: 6001, code: "Clinical Guidelines & Protocols", displayName: "Guidelines & Protocols", pmEnabled: false },
    { id: 6004, code: "Educational Resources", displayName: "Skill Building", pmEnabled: false },
    { id: 6005, code: "Expert Insights & Opinion", displayName: "Expert Insights", pmEnabled: false },
    { id: 6002, code: "Patient Case Studies & Real-world Evidence", displayName: "Case Studies & Evidence", pmEnabled: false },
    { id: 6006, code: "Patient Communication Resources", displayName: "Patient Communication", pmEnabled: false },
    { id: 6007, code: "Podcasts & Audio Learning", displayName: "Podcasts", pmEnabled: false }
  ];

  const contentType = newContentTypes.find(ct => ct.code === codeName);
  return contentType?.displayName || codeName;
};

const NewContentTypeSection = ({
  contentType,
  contentTypeId,
}: NewContentTypeSectionProps) => {
  const navigate = useNavigate();
  const [groupedContentByFormatData, setGroupedContentByFormatData] = useState<any>(null);
  const [transformedGroupedContentData, setTransformedGroupedContentData] = useState<any[]>([]);
  const [groupedContentLoading, setGroupedContentLoading] = useState<boolean>(false);
  const [groupedContentError, setGroupedContentError] = useState<string | null>(null);
  const [localLikes, setLocalLikes] = useState<number[]>([]);
  const [localBookmarks, setLocalBookmarks] = useState<number[]>([]);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [contentCounts, setContentCounts] = useState<ContentCounts>({});

  // Add a ref to track pending operations
  const pendingOperations = useRef<{ [key: string]: boolean }>({});
  
  // Add mounted ref to prevent API calls after unmount
  const isMounted = useRef(true);

  // Get store values
  const {
    tatvaPediaToken,
    tatvaPediaBookmarks,
    updateTatvaPediaBookmark,
    updateTatvaPediaLike,
    setTatvaPediaBookmarkedList,
    setTatvaPediaLikedList,
    pediaUserPreferences,
  } = useStore();

  const { doctorDetails, setDoctorDetails } = useStore((state) => ({
    doctorDetails: state.doctorDetails,
    setDoctorDetails: state.setDoctorDetails,
  }));

  // Extract speciality IDs from user preferences with useMemo to prevent unnecessary re-renders
  const specialityIds = useMemo(() => {
    const extractSpecialityIds = (specialitiesString: string) => {
      if (!specialitiesString) return [];
      
      // Extract the content within parentheses
      const match = specialitiesString.match(/speciality_id:\((.*?)\)/);
      if (!match || !match[1]) return [];
      
      // Split by "OR" and trim to get clean IDs
      return match[1].split("OR").map((id) => parseInt(id.trim()));
    };

    const ids = extractSpecialityIds(pediaUserPreferences?.specialities);
    console.log("NewContentTypeSection - specialityIds:", ids);
    console.log("NewContentTypeSection - pediaUserPreferences:", pediaUserPreferences);
    return ids;
  }, [pediaUserPreferences?.specialities]);

  // Determine if we're in the Podcasts section
  const isPodcastsSection = useMemo(() => {
    const consolidatedModuleId = Array.isArray(contentTypeId) ? contentTypeId[0] : contentTypeId;
    return consolidatedModuleId === 6007;
  }, [contentTypeId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initialize doctor details
  useEffect(() => {
    if (getLocalStorage(AUTH_TOKEN)) {
      const decodedToken = getDecodedToken();
      setDoctorDetails({ ...doctorDetails, ...decodedToken });
    }
  }, []);

  // Update local likes when bookmarks change
  useEffect(() => {
    if (!tatvaPediaBookmarks?.likedList || pendingOperations.current["like"])
      return;

    const likedIds = tatvaPediaBookmarks.likedList
      .filter((item: any) => item && item.contentId)
      .map((item: any) => item.contentId);

    setLocalLikes(likedIds);
  }, [tatvaPediaBookmarks.likedList]);

  // Update local bookmarks when bookmarks change
  useEffect(() => {
    if (
      !tatvaPediaBookmarks?.bookmarkedList ||
      pendingOperations.current["bookmark"]
    )
      return;

    const bookmarkedIds = tatvaPediaBookmarks.bookmarkedList
      .filter((item: any) => item && item.contentId)
      .map((item: any) => item.contentId);

    setLocalBookmarks(bookmarkedIds);
  }, [tatvaPediaBookmarks.bookmarkedList]);

  // Fetch initial liked and bookmarked content
  useEffect(() => {
    const fetchInitialState = async () => {
      if (!tatvaPediaToken && !localStorage.getItem("tatvaPediaToken")) return;

      try {
        const [likedResponse, bookmarkedResponse] = await Promise.all([
          getLikeBookmarkedContent("liked", tatvaPediaToken),
          getLikeBookmarkedContent("bookmarked", tatvaPediaToken),
        ]);

        if (likedResponse) {
          setTatvaPediaLikedList(likedResponse);
          const likedIds = likedResponse
            .filter((item: any) => item && item.contentId)
            .map((item: any) => item.contentId);
          setLocalLikes(likedIds);
        }

        if (bookmarkedResponse) {
          setTatvaPediaBookmarkedList(bookmarkedResponse);
          const bookmarkedIds = bookmarkedResponse
            .filter((item: any) => item && item.contentId)
            .map((item: any) => item.contentId);
          setLocalBookmarks(bookmarkedIds);
        }
      } catch (error) {
        console.error("Error fetching initial state:", error);
      }
    };

    fetchInitialState();
  }, [tatvaPediaToken, localStorage.getItem("tatvaPediaToken")]);

  // Function to fetch grouped content by format
  const fetchGroupedContentByFormat = useCallback(async (consolidatedModuleId: number) => {
    if (!isMounted.current) return;
    
    console.log("NewContentTypeSection - Calling API with specialityIds:", specialityIds);
    
    try {
      setGroupedContentLoading(true);
      setGroupedContentError(null);
      
      // For Podcasts section (6007), only show podcasts and videos, exclude documents and slides
      let contentFormatIds = [1000, 1001, 1002, 1003]; // Default format IDs
      if (consolidatedModuleId === 6007) {
        contentFormatIds = [1000, 1002]; // Only videos (1000) and podcasts (1002)
      }
      
      const response = await getGroupedContentByFormat({
        consolidatedModuleId,
        contentFormatIds,
        specialityIds,
        page: 0,
        pageSize: 8,
      });
      
      if (!isMounted.current) return;
      
      setGroupedContentByFormatData(response);
      
      // Transform the response to desired format
      const transformedData = transformGroupedContentByFormatResponse(response);
      setTransformedGroupedContentData(transformedData);
      
    } catch (error) {
      if (!isMounted.current) return;
      
      console.error("Error fetching grouped content by format:", error);
      setGroupedContentError(error instanceof Error ? error.message : "Failed to fetch grouped content by format");
    } finally {
      if (isMounted.current) {
        setGroupedContentLoading(false);
      }
    }
  }, [specialityIds]);

  // Fetch content on component mount
  useEffect(() => {
    if (contentTypeId) {
      const consolidatedModuleId = Array.isArray(contentTypeId) ? contentTypeId[0] : contentTypeId;
      fetchGroupedContentByFormat(consolidatedModuleId);
    }
  }, [contentTypeId, fetchGroupedContentByFormat, specialityIds]);

  // Add a ref to track the last fetch time and content IDs
  const lastFetchRef = useRef<{
    time: number;
    contentIds: string[];
  }>({
    time: 0,
    contentIds: [],
  });

  // Create a debounced version of the fetch function
  const debouncedFetchCounts = debounce(
    async (
      allContent: any[],
      authToken: string,
      lastFetchRef: React.MutableRefObject<{
        time: number;
        contentIds: string[];
      }>,
      setContentCounts: React.Dispatch<
        React.SetStateAction<{ [key: string]: any }>
      >
    ) => {
      try {
        if (allContent.length === 0) return;

        const currentContentIds = allContent
          .map((doc: any) => `${doc.docId || doc.id}_${doc.contentTypeId}`)
          .sort()
          .join(",");

        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchRef.current.time;
        const isSameContent =
          currentContentIds === lastFetchRef.current.contentIds.join(",");

        if (timeSinceLastFetch < 30000 && isSameContent) {
          return;
        }

        const contents = allContent.map((doc: any) => ({
          contentId: doc.docId || doc.id,
          contentTypeId: doc.contentTypeId,
        }));

        const countsResponse = await getContentCounts({
          contents,
          token: authToken,
          userId: doctorDetails?.user?.id,
        });

        if (countsResponse?.body) {
          const countsMap = countsResponse.body.reduce(
            (acc: any, item: any) => {
              const key = `${item.docId}_${item.docTypeId}`;
              acc[key] = {
                like_count: item.likeCount,
                isExternalSharable: item.isExternalSharable,
                uploadThumbnailUrl: item.uploadThumbnailUrl,
                previewUrl: item.previewUrl,
                readTime: item.readTime,
                duration: item.duration,
              };
              return acc;
            },
            {}
          );

          setContentCounts((prevCounts) => ({
            ...prevCounts,
            ...countsMap,
          }));

          lastFetchRef.current = {
            time: now,
            contentIds: currentContentIds.split(","),
          };
        }
      } catch (error) {
        console.error("Error fetching content counts:", error);
      }
    },
    1000
  );

  // Fetch content counts when data changes
  useEffect(() => {
    const authToken = getToken();
    if (!authToken) return;

    const allContent = transformedGroupedContentData || [];

    debouncedFetchCounts(allContent, authToken, lastFetchRef, setContentCounts);

    return () => {
      debouncedFetchCounts.cancel();
    };
  }, [transformedGroupedContentData, tatvaPediaToken]);

  const handleLike = async (content: any, isLiked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);
      pendingOperations.current["like"] = true;

      // Optimistically update UI
      setLocalLikes((prev) => {
        const newLikes = isLiked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newLikes;
      });

      // Update content counts
      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? -1 : 1),
          },
        };
      });

      // Make API call
      const response = await likeContent(
        content.docId,
        content.contentTypeId,
        !isLiked,
        token
      );

      if (response) {
        updateTatvaPediaLike(content, !isLiked);

        const updatedLikedContent = await getLikeBookmarkedContent(
          "liked",
          token
        );
        if (updatedLikedContent) {
          setTatvaPediaLikedList(updatedLikedContent);
        }
      } else {
        // Revert local states on failure
        setLocalLikes((prev) =>
          isLiked
            ? [...prev, content.docId]
            : prev.filter((id) => id !== content.docId)
        );

        setContentCounts((prev) => {
          const key = `${content.docId}_${content.contentTypeId}`;
          const currentCount = prev[key] || { like_count: 0 };
          return {
            ...prev,
            [key]: {
              ...currentCount,
              like_count: currentCount.like_count + (isLiked ? 1 : -1),
            },
          };
        });
      }
    } catch (error) {
      console.error("Error in like operation:", error);
      // Revert local states on error
      setLocalLikes((prev) =>
        isLiked
          ? [...prev, content.docId]
          : prev.filter((id) => id !== content.docId)
      );

      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? 1 : -1),
          },
        };
      });
    } finally {
      setIsActionLoading(false);
      setTimeout(() => {
        pendingOperations.current["like"] = false;
      }, 1500);
    }
  };

  const handleBookmark = async (content: any, isBookmarked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);
      pendingOperations.current["bookmark"] = true;

      // Optimistically update UI
      setLocalBookmarks((prev) => {
        const newBookmarks = isBookmarked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newBookmarks;
      });

      // Make API call
      const response = await bookmarkContent(
        content.docId,
        content.contentTypeId,
        !isBookmarked,
        token
      );

      if (response) {
        updateTatvaPediaBookmark(content, !isBookmarked);

        const updatedBookmarkedContent = await getLikeBookmarkedContent(
          "bookmarked",
          token
        );
        if (updatedBookmarkedContent) {
          setTatvaPediaBookmarkedList(updatedBookmarkedContent);
        }
      } else {
        // Revert local state on failure
        setLocalBookmarks((prev) =>
          isBookmarked
            ? [...prev, content.docId]
            : prev.filter((id) => id !== content.docId)
        );
      }
    } catch (error) {
      console.error("Error in bookmark operation:", error);
      // Revert local state on error
      setLocalBookmarks((prev) =>
        isBookmarked
          ? [...prev, content.docId]
          : prev.filter((id) => id !== content.docId)
      );
    } finally {
      setIsActionLoading(false);
      setTimeout(() => {
        pendingOperations.current["bookmark"] = false;
      }, 1500);
    }
  };

  const isItemLiked = (item: ContentItem) => {
    if (!item?.docId) return false;
    const isLiked = localLikes.includes(item.docId);
    return isLiked;
  };

  const isItemBookmarked = (item: ContentItem) => {
    if (!item?.docId) return false;
    const isBookmarked = localBookmarks.includes(item.docId);
    return isBookmarked;
  };

  // Helper function to get token
  const getToken = () =>
    tatvaPediaToken || localStorage.getItem("tatvaPediaToken");

  // Function to handle View More button clicks
  const handleViewMore = (type: string) => {
    const encodedContentType = encodeURIComponent(contentType || "");
    const moduleId = Array.isArray(contentTypeId) ? contentTypeId[0] : contentTypeId;
    
    navigate(
      `/tatvapedia/format-content-list?contentType=${encodedContentType}&contentTypeId=${moduleId}&type=${type}&previousTab=${encodeURIComponent(contentType || "")}`
    );
  };

  // Group content by format type
  const groupedContent = useMemo(() => {
    const grouped = {
      videos: [] as any[],
      documents: [] as any[],
      podcasts: [] as any[],
      slides: [] as any[],
    };

    transformedGroupedContentData.forEach((item) => {
      // Use contentFormat from API response - it's an array, so get the first element
      const contentFormat = Array.isArray(item.contentFormat) 
        ? item.contentFormat[0]?.toLowerCase() || ""
        : item.contentFormat?.toLowerCase() || "";
      
      if (contentFormat.includes("video")) {
        grouped.videos.push(item);
      } else if (contentFormat.includes("document")) {
        grouped.documents.push(item);
      } else if (contentFormat.includes("podcast")) {
        grouped.podcasts.push(item);
      } else if (contentFormat.includes("slide")) {
        grouped.slides.push(item);
      }
    });

    return grouped;
  }, [transformedGroupedContentData]);

  // Render Videos Section
  const renderVideosSection = () => {
    if (groupedContent.videos.length === 0) return null;

    return (
      <div className="px-4 mt-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center text-[#1F2933] font-poppins text-xl font-semibold leading-7">
            <PiNewspaperClipping
              fill="#A461D8"
              className="w-[1.5rem] h-[1.5rem] mr-2"
            />
            Videos
          </div>
          {groupedContent.videos.length > 3 && (
            <button
              onClick={() => handleViewMore("videos")}
              className="text-indigo-600 text-sm font-medium underline"
            >
              View More
            </button>
          )}
        </div>
        <div
          className="flex overflow-x-auto space-x-4 pb-4"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {groupedContent.videos.slice(0, 3).map((item: any) => {
            const countKey = `${item.docId}_${item.contentTypeId}`;
            const currentCounts = contentCounts[countKey] || {};

            return (
              <div key={item.id} className="flex-shrink-0">
                <TpSuqareCard
                  isShowcontentType={true}
                  isLike={isItemLiked(item)}
                  isBookmarked={isItemBookmarked(item)}
                  onLike={() => handleLike(item, isItemLiked(item))}
                  onBookmark={() =>
                    handleBookmark(item, isItemBookmarked(item))
                  }
                  data={{
                    docId: item.docId,
                    contentTitle: item.contentTitle,
                    contentType: Array.isArray(item.contentFormat) ? item.contentFormat[0] || "videos" : item.contentFormat || "videos", // Handle array format
                    uploadThumbnailUrl: item.uploadThumbnailUrl,
                    contentDescription: item.contentDescription,
                    previewDeepLink: item.previewDeepLink,
                    like_count:
                      currentCounts.like_count || item.like_count || 0,
                    contentTypeId: item.contentTypeId,
                    publishedDate: item.publishedDate,
                    isExternalSharable: currentCounts.isExternalSharable || 0,
                    previewUrl: currentCounts.previewUrl || "",
                    readTime:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.readTime || "",
                    duration:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.duration || "",
                    deepLinkCode: item.deepLinkCode,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Documents Section
  const renderDocumentsSection = () => {
    if (groupedContent.documents.length === 0) return null;

    return (
      <div className="px-4 mt-8">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center text-[#1F2933] font-poppins text-xl font-semibold leading-7">
            <PiNewspaperClipping
              fill="#A461D8"
              className="w-[1.5rem] h-[1.5rem] mr-2"
            />
            Documents
          </div>
          {groupedContent.documents.length > 3 && (
            <button
              onClick={() => handleViewMore("documents")}
              className="text-indigo-600 text-sm font-medium underline"
            >
              View More
            </button>
          )}
        </div>
        <div className="space-y-4">
          {groupedContent.documents.slice(0, 3).map((item: any) => {
            const countKey = `${item.docId}_${item.contentTypeId}`;
            const currentCounts = contentCounts[countKey] || {};

            return (
              <div key={item.id} className="pb-4">
                <TpRectangleCard
                  isShowcontentType={false}
                  isLike={isItemLiked(item)}
                  isBookmarked={isItemBookmarked(item)}
                  onLike={() => handleLike(item, isItemLiked(item))}
                  onBookmark={() =>
                    handleBookmark(item, isItemBookmarked(item))
                  }
                  data={{
                    docId: item.docId,
                    contentTitle: item.contentTitle,
                    contentType: Array.isArray(item.contentFormat) ? item.contentFormat[0] || "documents" : item.contentFormat || "documents", // Handle array format
                    uploadThumbnailUrl: item.uploadThumbnailUrl,
                    contentDescription: item.contentDescription,
                    previewDeepLink: item.previewDeepLink,
                    like_count:
                      currentCounts.like_count || item.like_count || 0,
                    contentTypeId: item.contentTypeId,
                    publishedDate: item.publishedDate,
                    isExternalSharable: currentCounts.isExternalSharable || 0,
                    previewUrl: currentCounts.previewUrl || "",
                    readTime:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.readTime || "",
                    duration:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.duration || "",
                    deepLinkCode: item.deepLinkCode,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Podcasts Section
  const renderPodcastsSection = () => {
    if (groupedContent.podcasts.length === 0) return null;

    return (
      <div className="px-4 mt-8">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center text-[#1F2933] font-poppins text-xl font-semibold leading-7">
            <PiNewspaperClipping
              fill="#A461D8"
              className="w-[1.5rem] h-[1.5rem] mr-2"
            />
            Podcasts
          </div>
          {groupedContent.podcasts.length > 3 && (
            <button
              onClick={() => handleViewMore("podcasts")}
              className="text-indigo-600 text-sm font-medium underline"
            >
              View More
            </button>
          )}
        </div>
        <div className="space-y-4">
          {groupedContent.podcasts.slice(0, 3).map((item: any) => {
            const countKey = `${item.docId}_${item.contentTypeId}`;
            const currentCounts = contentCounts[countKey] || {};

            return (
              <div key={item.id} className="pb-4">
                <TpRectangleCard
                  isShowcontentType={true}
                  isLike={isItemLiked(item)}
                  isBookmarked={isItemBookmarked(item)}
                  onLike={() => handleLike(item, isItemLiked(item))}
                  onBookmark={() =>
                    handleBookmark(item, isItemBookmarked(item))
                  }
                  data={{
                    docId: item.docId,
                    contentTitle: item.contentTitle,
                    contentType: Array.isArray(item.contentFormat) ? item.contentFormat[0] || "podcasts" : item.contentFormat || "podcasts", // Handle array format
                    uploadThumbnailUrl: item.uploadThumbnailUrl,
                    contentDescription: item.contentDescription,
                    previewDeepLink: item.previewDeepLink,
                    like_count:
                      currentCounts.like_count || item.like_count || 0,
                    contentTypeId: item.contentTypeId,
                    publishedDate: item.publishedDate,
                    isExternalSharable: currentCounts.isExternalSharable || 0,
                    previewUrl: currentCounts.previewUrl || "",
                    readTime:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.readTime || "",
                    duration:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.duration || "",
                    deepLinkCode: item.deepLinkCode,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Slides Section
  const renderSlidesSection = () => {
    if (groupedContent.slides.length === 0) return null;

    return (
      <div className="px-4 mt-8">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center text-[#1F2933] font-poppins text-xl font-semibold leading-7">
            <PiNewspaperClipping
              fill="#A461D8"
              className="w-[1.5rem] h-[1.5rem] mr-2"
            />
            Slides
          </div>
          {groupedContent.slides.length > 3 && (
            <button
              onClick={() => handleViewMore("slides")}
              className="text-indigo-600 text-sm font-medium underline"
            >
              View More
            </button>
          )}
        </div>
        <div className="space-y-4">
          {groupedContent.slides.slice(0, 3).map((item: any) => {
            const countKey = `${item.docId}_${item.contentTypeId}`;
            const currentCounts = contentCounts[countKey] || {};

            return (
              <div key={item.id} className="pb-4">
                <TpRectangleCard
                  isShowcontentType={true}
                  isLike={isItemLiked(item)}
                  isBookmarked={isItemBookmarked(item)}
                  onLike={() => handleLike(item, isItemLiked(item))}
                  onBookmark={() =>
                    handleBookmark(item, isItemBookmarked(item))
                  }
                  data={{
                    docId: item.docId,
                    contentTitle: item.contentTitle,
                    contentType: Array.isArray(item.contentFormat) ? item.contentFormat[0] || "slides" : item.contentFormat || "slides", // Handle array format
                    uploadThumbnailUrl: item.uploadThumbnailUrl,
                    contentDescription: item.contentDescription,
                    previewDeepLink: item.previewDeepLink,
                    like_count:
                      currentCounts.like_count || item.like_count || 0,
                    contentTypeId: item.contentTypeId,
                    publishedDate: item.publishedDate,
                    isExternalSharable: currentCounts.isExternalSharable || 0,
                    previewUrl: currentCounts.previewUrl || "",
                    readTime:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.readTime || "",
                    duration:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.duration || "",
                    deepLinkCode: item.deepLinkCode,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (groupedContentLoading) {
    return <LoadingSkeleton />;
  }

  // Render error state
  if (groupedContentError) {
    return (
      <div className="flex-1 overflow-auto pb-16 flex items-center justify-center">
        <div className="text-lg text-red-500">Error: {groupedContentError}</div>
      </div>
    );
  }

  // Render empty state
  if (!transformedGroupedContentData.length) {
    return (
      <div className="flex-1 overflow-auto pb-16 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center px-4 py-4 bg-[#fff] rounded-3xl m-3 pt-8">
          <img
            src={EmptyState}
            alt="No content available"
            className="w-32 h-32 mb-4"
          />
          <p className="text-center">
            <span className="text-[#6e6e74] font-normal">
              There's no content available right now.
            </span>
          </p>
          <p className="text-[#6e6e74] mt-2">Stay tuned for updates!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto pb-16">
      {(isActionLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      )}
      
      {/* Render sections based on content type */}
      {renderVideosSection()}
      {!isPodcastsSection && renderDocumentsSection()}
      {renderPodcastsSection()}
      {!isPodcastsSection && renderSlidesSection()}
    </div>
  );
};

export default NewContentTypeSection;
